import { PText } from "@porsche-design-system/components-react"
import { getOfferOverviewRoute } from "@slfinrtl/ui-bff-routes"
import type { DosOfferOverviewView } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useMemo, useState } from "react"
import { FormattedMessage, defineMessages, useIntl } from "react-intl"
import { useNavigate } from "react-router"
import { useDealerAppRoutes } from "src/common/routes/useDealerAppRoutes"
import { ActionItem, Stage } from "src/components"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useModal } from "src/context/modal/useModal"
import { CreateOfferModalContainer, useBff } from "src/hooks"
import { useVehicleDetailsContext } from "../../context/VehicleDetailsContext"
import { OfferList } from "./components/offerList/OfferList"
import { OfferListSkeleton } from "./components/offerList/OfferListSkeleton"

const messages = defineMessages({
	noOfferTitle: {
		defaultMessage: "There are currently no existing offers for this vehicle",
		id: "vehicleDetails.salesActivity.offerOverview.noOffer",
	},
	offerOverviewHeadline: {
		defaultMessage: "Offer Overview",
		id: "vehicleDetails.salesActivity.offerOverview.headline",
	},
})

export const OfferOverviewContainer = withErrorHandler(() => {
	const [offerOverviewView, setOfferOverviewView] = useState<DosOfferOverviewView | "irrelevant" | null | undefined>(
		undefined,
	)
	const { inventoryId, subscribe } = useVehicleDetailsContext()
	const { formatMessage } = useIntl()
	const navigate = useNavigate()
	const showCreateOfferModal = useModal(CreateOfferModalContainer)
	const routes = useDealerAppRoutes()

	const fetchOfferOverviewView = useBff(
		getOfferOverviewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)
	useEffect(() => {
		fetchOfferOverviewView().then(setOfferOverviewView)
	}, [fetchOfferOverviewView])

	useEffect(() => {
		subscribe("saleCancelled", () => fetchOfferOverviewView().then(setOfferOverviewView))
	}, [fetchOfferOverviewView, subscribe])

	const onCreateOfferClicked = useCallback(
		() => showCreateOfferModal({ vehicleKey: inventoryId }),
		[inventoryId, showCreateOfferModal],
	)
	const onEditOfferClicked = useMemo(() => {
		const vehicleDetails = routes?.inventoryList?.vehicleDetails
		return vehicleDetails
			? (offerId: string) => navigate(vehicleDetails.editOffer.url(inventoryId, offerId))
			: undefined
	}, [inventoryId, navigate, routes?.inventoryList?.vehicleDetails])

	if (offerOverviewView === "irrelevant") {
		return null
	}

	return offerOverviewView ? (
		<Stage
			title={formatMessage(messages.offerOverviewHeadline)}
			actionButtons={
				<ActionItem
					key="createOffer"
					onClick={onCreateOfferClicked}
					view={offerOverviewView.createOfferAction}
				/>
			}
		>
			<ErrorNotificationInline />
			{offerOverviewView.offers.length && onEditOfferClicked ? (
				<OfferList offers={offerOverviewView.offers} onEditOfferClicked={onEditOfferClicked} />
			) : (
				<PText>
					<FormattedMessage {...messages.noOfferTitle} />
				</PText>
			)}
		</Stage>
	) : (
		<Stage title={formatMessage(messages.offerOverviewHeadline)}>
			<OfferListSkeleton />
		</Stage>
	)
})
